import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { POLLING_INTERVAL, claimStatusEnum } from 'common/utils/enums';
import ClaimsTable from 'components/claims/claims-table';
import EmptyClaims from 'components/empty-pages/empty-claims';
import DashboardWrapperLayout from 'layouts/dashboard-wrapper';
import TablePaginateWrapper from 'common/layouts/table-paginate-wrapper';
import { useGetAllClaimsQuery } from 'redux/api/ciosUsersApi/ciosUsersApi';
import { isEmpty } from 'common/utils/isEmpty';
import { ButtonGroup, Flex } from '@chakra-ui/react';
import ContactCard from 'components/shared/contact-card';
import CTA_Buttons from 'common/components/cta-buttons';
import EmptyPage from 'common/components/empty-page';
import Button from 'common/components/button';
import ContactSupportModal from 'components/shared/help-button';

const Claims = () => {
  // DATA INITIALIZATION
  const pageSize = 5;
  const statuses = [
    claimStatusEnum.SUBMITTED,
    claimStatusEnum.IN_REVIEW,
    claimStatusEnum.RESOLVED,
    claimStatusEnum.APPROVED,
    claimStatusEnum.CONFIRMED,
    claimStatusEnum.COMPLETED,
  ];

  // STATES
  const [currentActivePage, setCurrentActivePage] = useState(1);
  const [currentDraftPage, setCurrentDraftPage] = useState(1);
  const [pollingInterval, setPollingInterval] = useState(
    () => POLLING_INTERVAL
  );
  const [sortVal, setSort] = useState('-statusChanges.updatedAt');

  // HELPERS
  const calculateMinMaxPageLimit = (currentPage, pageSize) => {
    const minPageLimit = (currentPage - 1) * pageSize;
    const maxPageLimit = currentPage * pageSize - 1;
    return [minPageLimit, maxPageLimit];
  };

  // HOOKS
  const { data: claimsData, isLoading: isGetAllClaimsLoading } =
    useGetAllClaimsQuery(
      {
        pollingInterval: pollingInterval,
        sort: sortVal,
        status: statuses,
        limit: pageSize,
      },
      {
        refetchOnMountOrArgChange: true,
      }
    );

  const [activeMinPageLimit, activeMaxPageLimit] = useMemo(() => {
    return calculateMinMaxPageLimit(currentActivePage, pageSize);
  }, [currentActivePage]);

  const [draftMinPageLimit, draftMaxPageLimit] = useMemo(() => {
    return calculateMinMaxPageLimit(currentDraftPage, pageSize);
  }, [currentDraftPage]);

  const navigate = useNavigate();

  // Filter claimsData down to only the claims that are in the statuses array, and have a statusChanges array that is not empty
  // Then sort by the last item in statusChanges
  const filteredClaimsData = useMemo(() => {
    if (isEmpty(claimsData)) return [];
    return claimsData
      .filter((claim) => statuses.includes(claim.status))
      .sort((a, b) => {
        const aLast = a.statusChanges[a.statusChanges.length - 1]?.updatedAt;
        const bLast = b.statusChanges[b.statusChanges.length - 1]?.updatedAt;

        if (!aLast && !bLast) return 0;
        if (!aLast) return 1;
        if (!bLast) return -1;

        return new Date(bLast) - new Date(aLast);
      });
  }, [claimsData]);

  const draftClaimsData = useMemo(() => {
    if (isEmpty(claimsData)) return [];
    return claimsData.filter((claim) => claim.status === claimStatusEnum.DRAFT);
  }, [claimsData]);

  // USE EFFECT
  useEffect(() => {
    if (isEmpty(claimsData)) {
      setPollingInterval(0);
    } else {
      setPollingInterval(POLLING_INTERVAL);
    }
  }, [claimsData]);

  // Set up tabs for page state
  // Set up url query params for activeTab
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(activeTabFromUrl || 'submitted');

  // keep URL in sync with active tab
  useEffect(() => {
    // merge only the 'tab' param, preserving others, avoid flicker on identical value
    if (searchParams.get('tab') !== activeTab) {
      const params = new URLSearchParams(searchParams);
      params.set('tab', activeTab);
      setSearchParams(params, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  const tabs = [
    {
      title: 'Submitted',
      selected: activeTab === 'submitted',
      onClick: () => {
        setActiveTab('submitted');
      },
    },
    {
      title: 'Draft',
      selected: activeTab === 'draft',
      onClick: () => {
        setActiveTab('draft');
      },
    },
  ];

  const buttons = (
    <ButtonGroup>
      <Button
        onClick={() => navigate('/dashboard/claims/submit')}
        theme='primary'
        title='Submit New Claim'
        wFull={false}
      />
      <ContactSupportModal
        buttonProps={{
          title: 'Get Help',
          theme: 'secondary',
        }}
        modalHeader='Contact Support'
      />
    </ButtonGroup>
  );

  return (
    <DashboardWrapperLayout title={'Claims'} buttons={buttons} tabs={tabs}>
      {/* Main Container */}
      <div className='flex flex-col w-full gap-12'>
        {/* table */}
        {isGetAllClaimsLoading ? (
          <EmptyClaims isLoading={isGetAllClaimsLoading} />
        ) : isEmpty(filteredClaimsData) ? (
          <EmptyClaims />
        ) : (
          <>
            {activeTab === 'submitted' ? (
              filteredClaimsData.length > 0 ? (
                <TablePaginateWrapper
                  setCurrentPage={setCurrentActivePage}
                  currentPage={currentActivePage}
                  totalCount={filteredClaimsData?.length}
                >
                  <ClaimsTable
                    claims={filteredClaimsData?.slice(
                      activeMinPageLimit,
                      activeMaxPageLimit
                    )}
                    statuses={statuses}
                  />
                </TablePaginateWrapper>
              ) : (
                <EmptyPage
                  title='No Submitted Claims'
                  subtitle='You have no submitted claims at this time.'
                  buttonText={'Submit New Claim'}
                  onClick={() => setActiveTab('draft')}
                />
              )
            ) : activeTab === 'draft' ? (
              draftClaimsData.length > 0 ? (
                <TablePaginateWrapper
                  setCurrentPage={setCurrentDraftPage}
                  currentPage={currentDraftPage}
                  totalCount={draftClaimsData?.length}
                >
                  <ClaimsTable
                    claims={draftClaimsData?.slice(
                      draftMinPageLimit,
                      draftMaxPageLimit
                    )}
                    statuses={statuses}
                  />
                </TablePaginateWrapper>
              ) : (
                <EmptyPage
                  title='No Draft Claims'
                  subtitle='You have no draft claims at this time.'
                  buttonText={'Submit New Claim'}
                  onClick={() => navigate('/dashboard/claims/submit')}
                />
              )
            ) : null}
          </>
        )}

        {/* help */}
        <ContactCard title='Need Help With a Claim?'>
          Contact us by email, phone, or text and we can help you answer any
          questions you may have about your claim.
        </ContactCard>
      </div>
    </DashboardWrapperLayout>
  );
};

export default Claims;
